"use client";

import { useCallback, useId, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { MagnifyingGlass } from "@phosphor-icons/react";
import SearchAutocomplete, { type SearchAutocompleteHandle } from "@/components/ui/SearchAutocomplete";
import type { SuggestionCounts, SpecialtySuggestion } from "@/lib/queries/professionals";
import { miniChatDesktopSuppressed, miniSearchSuppressed } from "@/lib/chat/mini-chat-routes";
import { useReducedMotion } from "@/lib/hooks/use-reduced-motion";
import { useAppFrameMounted } from "@/lib/app-frame-store";
import { localeHref } from "@/lib/locale-href";
import { BELOW_MD_MQ } from "@/lib/breakpoints";
import { SEARCH_COMPOSER_OPEN_EVENT } from "@/lib/events";
import { CHAT_EVENTS, track } from "@/lib/analytics";
import MiniBarShell, { useHeroRevealGate } from "./MiniBarShell";

interface MiniSearchBarProps {
  /**
   * ⛔ REQUIRED, both of them. An optional `counts?` with a `{}` default is
   * exactly what let the homepage's mount render without them for an afternoon:
   * it compiled, and the bar offered six cities badged "0 results" beside a hero
   * that offered one (owner, 2026-09-08). A missing prop must be a compile
   * error here, not a quietly degraded control.
   */
  counts: SuggestionCounts;
  specialties: SpecialtySuggestion[];
}

/**
 * The docked bar when the assistant is unavailable — a search field instead of
 * nothing (`mini-search-fallback-spec.md`). Same pill, same position, same
 * reveal rules; only the contents and the submit behaviour differ.
 *
 * ⭐ IT NEVER DISAPPEARS. That is the whole point: `MiniChat` used to return
 * `null` when the assistant was off, and the bottom-of-viewport slot simply
 * went empty, leaving the reader with no persistent way to find anyone.
 *
 * ⭐ SINGLE LINE, AND IT DOES NOT EXPAND (owner D2). The assistant's panel grows
 * upward to offer starter prompts; a search box has none, so there is nothing to
 * grow into.
 *
 * ⭐ THE MARK IS A MAGNIFIER, NOT THE ORB (owner D3). `LiquidOrb` is the
 * assistant's mark; wearing it on a control that runs a keyword search
 * misrepresents what the control does.
 *
 * ⭐ Signed-out readers see it (owner D4) — search has never required auth, and
 * a fallback that hides itself from half the audience is not a fallback.
 *
 * ⛔ It says NOTHING about why the assistant is absent. The standing rule
 * collapses every unavailability into one generic line precisely so we do not
 * leak operational state; "the assistant ran out of credit" is exactly that. A
 * search box that simply works is a better answer than an apology.
 */
export default function MiniSearchBar({ counts, specialties }: MiniSearchBarProps) {
  const t = useTranslations("chat.mini");
  const tSearch = useTranslations("search");
  // Reuses the hero's label rather than minting a second string: the wording is
  // exactly right for this control and its VI is translator-reviewed, where a
  // new key would add another unreviewed machine draft.
  const tHome = useTranslations("home");
  const locale = useLocale();
  const router = useRouter();
  const reducedMotion = useReducedMotion();
  // `/chat` is an app frame with its own pinned composer — the frame declares
  // itself, so this needs no route test (which would go stale on client-side
  // navigation, the root layout never being remounted).
  const appFrame = useAppFrameMounted();
  const pathname = usePathname();
  const fieldRef = useRef<SearchAutocompleteHandle>(null);
  /**
   * ⛔ UNIQUE PER INSTANCE, not the constant it used to be. This component has
   * TWO mount sites — the homepage's own `<MiniChat>` and the marketing
   * layout's — and both copies stay in the DOM after a client-side navigation
   * (measured: two `#mini-search` inputs and two listboxes still present 4s
   * after the transition settled). A fixed id makes that invalid HTML and makes
   * `aria-controls` / `aria-activedescendant`, which resolve first-in-document
   * -order, point at whichever copy the App Router happened to leave first.
   * `useId` is stable across SSR and hydration, so it costs nothing.
   */
  const fieldId = `mini-search-${useId()}`;

  // ⭐ Suppressed on `/search` at EVERY width (owner D1), not just `lg`+ like the
  // assistant bar. A docked search box on the search results page offers the
  // reader the page they are already standing on. The `lg`+ rule still covers a
  // professional profile, where the contact card is the action.
  //
  // Computed HERE rather than at the early return below so the reveal gate can
  // be switched off with it: hooks cannot sit under a `return null`, and
  // `/search` is the page most likely to be scrolled hard.
  const suppressed = miniSearchSuppressed(pathname);

  // ⭐ The reveal gate is the SAME one the assistant bar uses, and it does the
  // load-bearing work here for free: when the assistant is off, the hero *is* a
  // search input carrying `[data-hero-search]`, so this stays hidden while that
  // hero is on screen. Two search boxes in one viewport is the exact
  // duplication the gate exists to stop.
  const revealed = useHeroRevealGate(pathname, !suppressed);

  /**
   * ⛔ NEVER YANK IT AWAY MID-INTERACTION — the same promise `MiniChatBar` makes,
   * which it keeps for free because focusing it sets `expanded` and that pins
   * the bar on screen. This variant has no expanded state, so it needs its own
   * latch, and without one it was measurably broken: on the homepage, scroll
   * past the hero, type into the docked search, then scroll back up — the hero
   * re-enters the viewport, `revealed` flips, and the pill slid out with the
   * reader's query still in it, dropping keyboard focus onto a
   * `visibility: hidden` element (measured 2026-09-08).
   *
   * Focus is the signal, and it is read on the whole ROW so that tabbing from
   * the field to the submit button does not read as leaving.
   */
  const [engaged, setEngaged] = useState(false);
  // Reset when the reader moves on. The marketing layout is never remounted
  // across client navigation, so a latch left true would follow them to the
  // next page and hold the bar open over its hero. Adjusted during render —
  // React's own "reset state when a value changes" pattern, same as the
  // assistant bar's.
  const [lastPath, setLastPath] = useState(pathname);
  if (pathname !== lastPath) {
    setLastPath(pathname);
    setEngaged(false);
  }

  /**
   * Where a free-text submit goes, decided at the moment of the tap.
   *
   * ⛔ NOT `SEARCH_COMPOSER_OPEN_EVENT`'s cancel signal, which looks like it
   * already answers this and does not. `SearchComposer` is `md:hidden` — MOUNTED
   * at every width and merely CSS-hidden above 768px — so its listener calls
   * `preventDefault()` on desktop too. The cancel signal means MOUNTED, not
   * VISIBLE; using it would give a dead control above 768px, which is the precise
   * failure that mechanism exists to prevent.
   *
   * ⛔ NOT `MOBILE_MQ` either — that is `sm` (640px) and the composer's gate is
   * `md` (768px), leaving a 128px band where the bar opens a drawer that is not
   * rendered. `BELOW_MD_MQ` is the composer's own threshold.
   *
   * Read per tap rather than subscribed: the control looks identical either way
   * (D2), so there is nothing to re-render, and a live reading cannot go stale.
   */
  const belowMd = () =>
    typeof window !== "undefined" && window.matchMedia(BELOW_MD_MQ).matches;

  const submit = useCallback((raw: string) => {
    const q = raw.trim();
    // ⛔ CAPTURED BEFORE ANY NAVIGATION. The analytics call moved below the
    // `router.push` so it could report the OUTCOME rather than the prediction —
    // but reading `window.location` there means the path can be the DESTINATION
    // once a push commits, and the question this event answers is where the
    // reader searched FROM.
    const path = window.location.pathname;
    const href = localeHref(q ? `/search?q=${encodeURIComponent(q)}` : "/search", locale);
    // ⛔ `target` is the OUTCOME, resolved below, never the prediction. Firing
    // the event up front recorded "composer" for the fall-through case too — so
    // the one case where the two destinations diverge was the one case the field
    // that exists to tell them apart got wrong.
    let target: "composer" | "search" = "search";
    if (belowMd()) {
      // Below `md` the drawer is the site's established search surface and gives
      // filters in one tap. It seeds itself from the URL, so the typed text
      // rides along in `detail` or it would be silently discarded on the way in.
      const ev = new CustomEvent(SEARCH_COMPOSER_OPEN_EVENT, { cancelable: true, detail: { q } });
      window.dispatchEvent(ev);
      // Here the cancel signal IS the right question — "was anything listening
      // at all" — and it is the reason the event is cancelable. It is only a
      // useless answer to "is this mobile", above. The composer is mounted
      // everywhere this bar renders, so this is a belt-and-braces path: a dead
      // tap is the one outcome a fallback control must never produce.
      if (ev.defaultPrevented) target = "composer";
      else router.push(href);
    } else {
      // At `md` and up the drawer does not exist and `/search` has a full sidebar.
      router.push(href);
    }
    track(CHAT_EVENTS.miniSearchSubmitted, {
      path,
      chars: q.length, // a LENGTH, never the text itself
      target,
    });
  }, [router, locale]);

  // Never over the assistant's own page.
  if (appFrame) return null;
  if (suppressed) return null;

  return (
    <MiniBarShell
      onScreen={revealed || engaged}
      reducedMotion={reducedMotion}
      desktopSuppressed={miniChatDesktopSuppressed(pathname)}
      // ⛔ The pill must NOT clip here. The assistant's panel is `overflow-hidden`
      // because it grows a square-cornered body inside a round box; this one
      // instead hangs a dropdown ABOVE itself, and clipping would render the
      // suggestions invisible with nothing to show for the query.
      clip={false}
    >
      <div
        className="flex items-center gap-10 p-10 pl-13"
        // Focus events bubble in React (focusin/focusout), so the whole row is
        // the unit: moving between the field, the dropdown and the submit
        // button all stay "engaged". `relatedTarget` is where focus went — null
        // (to the page chrome) or outside the row both end the interaction.
        onFocusCapture={() => setEngaged(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setEngaged(false);
        }}
      >
        {/* The mark. A magnifier, because that is what this control does. */}
        <span aria-hidden="true" className="grid h-28 w-28 shrink-0 place-items-center text-navy/40">
          <MagnifyingGlass size={18} weight="bold" />
        </span>

        <span className="min-w-0 flex-1">
          <SearchAutocomplete
            // ⭐ Reset between pages. This is mounted in the marketing LAYOUT,
            // which is never remounted across client navigation, so without a
            // key the bar carries the previous page's query around the site —
            // the same reason the assistant bar resets its textarea on a path
            // change. The component is uncontrolled, so remounting is the reset.
            key={pathname}
            ref={fieldRef}
            id={fieldId}
            counts={counts}
            specialties={specialties}
            placeholder={t("searchPlaceholder")}
            label={tHome("searchLabel")}
            // The pill already draws the border and the ground; the field is
            // just the text. Mirrors the assistant textarea's treatment.
            inputClassName="block w-full bg-transparent text-sm leading-snug text-navy outline-none placeholder:text-gray-500 [&::-webkit-search-cancel-button]:hidden"
            // ⛔ Opens UPWARD. The bar is docked to the bottom edge, so the
            // default `top-full` dropdown would render off-screen.
            //
            // ⚠️ `bottom-full` anchors to the INPUT, not to the pill, and the
            // input is inset by the row's `p-10` plus its own centring inside
            // the 34px button's line — MEASURED at 18.4px. So `mb-8` put the
            // dropdown's bottom edge 10.4px INSIDE the pill, sitting on the
            // search bar (owner caught it). The offset has to clear that inset
            // before it buys any daylight: `mb-34` leaves ~15.6px of real gap.
            dropdownClassName="absolute bottom-full left-0 right-0 z-dropdown mb-34 max-h-280 overflow-y-auto scrollbar-thin rounded-pop border border-card-line bg-white shadow-e4"
            // The gold button beside it is the submit affordance; the
            // component's own right-hand icon would be a second one.
            showRightIcon={false}
            onSelect={({ intent, href, query, type }) => {
              if (intent === "suggestion" && href) {
                track(CHAT_EVENTS.miniSearchSuggestion, { path: window.location.pathname, type });
                router.push(localeHref(href, locale));
                return;
              }
              submit(query);
            }}
          />
        </span>

        <button
          type="button"
          // The field is uncontrolled, so its value comes from the handle
          // rather than a mirrored piece of state that could drift from it.
          onClick={() => submit(fieldRef.current?.getValue() ?? "")}
          aria-label={tSearch("searchAria")}
          className="grid h-34 w-34 shrink-0 place-items-center rounded-full bg-gold text-navy transition-colors hover:bg-gold-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M8 13V3M3.5 7.5L8 3l4.5 4.5" />
          </svg>
        </button>
      </div>
    </MiniBarShell>
  );
}
